import { DOCUMENT, NgClass } from '@angular/common';
import { AfterViewInit, Component, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AxisLabelsFormatterContextObject,
  Point,
  Series,
  SeriesLegendItemClickEventObject,
  SeriesOptionsType,
  YAxisOptions,
} from 'highcharts';
import * as Highstock from 'highcharts/highstock';
import { LongDateFormatKey } from 'moment';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { AccountDto, SiteDto, Timestamp } from '@dto';
import { SeriesOptions } from '@models';
import { LoaderService, SidenavService, TimezonesService } from '@services';

@Component({
  imports: [TranslatePipe, NgClass],
  selector: 'monitor-ui-multi-stat-chart',
  styleUrls: ['./multi-stat-chart.component.scss'],
  templateUrl: './multi-stat-chart.component.html',
})
export class MultiStatChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  activeTabIndex = 0;
  chart: Highstock.Chart;
  @Input() chartId = 'multi-stat-container';
  @Input() colors: string[];
  @Input() containerSelector: string | undefined;
  @Input() entity: SiteDto | AccountDto;
  locale = 'en_US';
  @Input() maxDate: number | undefined;
  @Input() minDate: number | undefined;
  series: SeriesOptions[] = [];
  @Input() seriesLabels: string[] = [''];
  @Input() stats: Timestamp[][];
  @Input() tabs: {
    title: string;
    stats: Timestamp[][];
    labels: string[];
    colors: string[];
    units: string;
  }[];
  timezone = 'America/New_York';
  @Input() titleKey = '';
  @Input() unit = 'kW';
  private _onDestroy = new Subject<void>();

  constructor(
    public timezoneService: TimezonesService,
    private translateService: TranslateService,
    private sidenavService: SidenavService,
    private loaderService: LoaderService,
    private zone: NgZone,
    @Inject(DOCUMENT) private _document: Document,
  ) {
    this.sidenavService.toggle$.pipe(takeUntil(this._onDestroy)).subscribe(() => setTimeout(() => this.setOptions()));
    fromEvent(window, 'resize')
      .pipe(debounceTime(100), takeUntil(this._onDestroy))
      .subscribe(() => this.setOptions());
  }

  displayDateTime = (data: number | string, dateTimeFormat: LongDateFormatKey): string => {
    return this.timezoneService.getDateTimeDisplayLabel(data as string, dateTimeFormat, 'America/New_York', true);
  };

  hidden(name: string): boolean {
    name = this.serieNameCleaner(name);
    const series = this.chart.xAxis[0].series;
    return !series.find(serie => this.serieNameCleaner(serie.name) === name).visible;
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(async () => {
      await this.setOptions();
      this.chart?.redraw?.();
      this.loaderService.end();
    });
  }

  async ngOnChanges(): Promise<void> {
    await this.setOptions();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
  }

  ngOnInit(): void {
    this.locale = 'en_US';
    this.timezone = this.timezoneService.getTimezoneName(this.timezone);
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    this.setOptions();
  }

  serieNameCleaner = (name: string): string => {
    name = name.replaceAll(' ', '');
    name = name.replaceAll(/[/&]/g, '');
    if (/^\d+$/.test(name[0])) {
      return name.replaceAll(/[0-9]/g, '');
    } else {
      return name;
    }
  };

  seriesDistinct(): string[] {
    const series = this.series;
    return [...new Set(series.map(serie => serie.name))];
  }

  toggleSerie(name: string): void {
    name = this.serieNameCleaner(name);
    const series = this.chart.xAxis[0].series;
    series
      .filter(serie => this.serieNameCleaner(serie.name) === name)
      .forEach(serie => {
        if (serie.visible) {
          serie.hide();
        } else {
          serie.show();
        }
      });
  }

  tooltipFormatter = (data: Point): string => {
    const dateHtml = this.displayDateTime(data.x, 'lll');
    const containerStyle = 'width: fit-content; background-color: #fff';
    const dateStyle = 'font-size: 13px; margin-bottom: 0.5rem; display: block';
    const h2Style = `font-size: 15px; margin-right: 0.5rem; display: inline-block;`;
    const kwStyle = `font-size: 15px; display: inline-block; font-weight: bold`;
    let tooltipContent = '';
    const alreadyAdded: string[] = [];
    this.series.forEach(option => {
      if (alreadyAdded.includes(option.name)) {
        return;
      } else {
        alreadyAdded.push(option.name);
      }
      const points = data.points.find(series => series.series.name === option.name);
      const containerDisplay = points ? 'block' : 'none';
      const divStyle = `display: inline-block; width: 0.75rem; height: 0.75rem; background: ${option.color}; border-radius: 50%; margin-right: 0.25rem`;
      tooltipContent += `
        <div style="display: ${containerDisplay}">
          <div style="${divStyle}"></div>
          <span style="${h2Style}">
            ${option.name} |
          </span>
          <span style="${kwStyle}">${points?.y.toFixed(2)} ${option.tooltipUnit}</span>
        </div>
      `;
    });
    return `
      <div style="${containerStyle}">
        <span style="${dateStyle}" data-automation="highchart-tooltip">${dateHtml}</span>
        ${tooltipContent}
      </div>
    `;
  };

  xAxisFormatter = (data: AxisLabelsFormatterContextObject): string => {
    return this.displayDateTime(data.value, 'lll');
  };

  private async chartSeries(): Promise<SeriesOptions[]> {
    const series: SeriesOptions[] = [];
    if (!this.tabs && (this.stats.length !== this.seriesLabels.length || this.stats.length !== this.colors.length)) {
      console.warn('Must provide a seriesLabel and color for every series in the stats property');
      return [];
    }
    this.stats.forEach((stat, index) => {
      const chartData: (number | string)[][] = stat.map(point => {
        return [point.timestamp, point.value];
      });
      let opt: SeriesOptions = {
        active: true,
        color: this.colors[index],
        data: chartData,
        name: this.seriesLabels[index],
        showInLegend: false,
        showInNavigator: true,
        tooltipUnit: this.tabs ? this.tabs[this.activeTabIndex].units : this.unit,
        type: 'line',
        visible: true,
        yAxis: 0,
      };
      const isFutureSerie = series.some(
        serie => this.serieNameCleaner(serie.name) === this.serieNameCleaner(this.seriesLabels[index]),
      );
      if (isFutureSerie) {
        opt = { ...opt, dashStyle: 'shortdash' };
      }
      series.push(opt);
    });
    return series;
  }

  private getYAxis(unit: string): YAxisOptions[] {
    return [
      {
        alignTicks: false,
        endOnTick: true,
        labels: {
          format: `{value} ${unit}`,
        },
        maxPadding: 0,
        minPadding: 0,
        opposite: false,
        showFirstLabel: true,
        showLastLabel: true,
        softMin: 0,
        startOnTick: false,
        title: {
          text: this.translateService.instant(this.titleKey),
          textAlign: 'center',
        },
        visible: true,
      },
    ];
  }

  private async setOptions(): Promise<void> {
    let wrapperElement = this._document.querySelector('.overview-container');
    if (!wrapperElement && this.tabs) {
      setTimeout(() => this.setOptions(), 100);
      return;
    }
    if (!this.tabs) {
      if (!this.containerSelector) {
        console.warn('Must provide a container class');
      }
      wrapperElement = this._document.querySelector(this.containerSelector);
      if (!wrapperElement) {
        setTimeout(() => this.setOptions(), 100);
        return;
      }
    }
    let tabsElement: HTMLDivElement;
    let tabsMargin: number;
    let containerElement: HTMLDivElement;
    let containerPadding: number;
    if (this.tabs) {
      tabsElement = this._document.querySelector('.tabs') as HTMLDivElement;
      tabsMargin = parseFloat(getComputedStyle(tabsElement).marginRight);
      this.seriesLabels = this.tabs[this.activeTabIndex].labels;
      this.stats = this.tabs[this.activeTabIndex].stats;
      this.colors = this.tabs[this.activeTabIndex].colors;
      this.titleKey = this.tabs[this.activeTabIndex].title;
    } else {
      containerElement = this._document.querySelector(this.containerSelector) as HTMLDivElement;
      containerPadding =
        parseFloat(getComputedStyle(containerElement).paddingRight) +
        parseFloat(getComputedStyle(containerElement).paddingLeft);
    }
    this.series = await this.chartSeries();
    const tooltipFormatter = this.tooltipFormatter;
    const displayDateTime = this.displayDateTime;
    const unit = this.tabs ? this.tabs[this.activeTabIndex].units : this.unit;

    this.chart = Highstock.chart({
      boost: {
        useGPUTranslations: true,
        usePreallocated: true,
      },
      chart: {
        animation: false,
        height: 500,
        renderTo: this.chartId,
        style: {
          fontFamily: 'var(--fonts)',
        },
        type: 'line',
        width: wrapperElement.clientWidth - (tabsElement ? tabsElement.clientWidth + tabsMargin * 2 : containerPadding),
        zooming: {
          resetButton: {
            position: {
              x: -10,
              y: 20,
            },
          },
          type: 'x',
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: true,
        itemStyle: {
          fontFamily: 'var(--font)',
        },
      },
      mapNavigation: {
        enableMouseWheelZoom: true,
      },
      // navigator: {
      //   adaptToUpdatedData: false,
      //   enabled: true,
      //   handles: {
      //     borderColor: '#d9d9d9',
      //   },
      //   maskFill: 'rgba(102,133,194,0.2)',
      //   outlineColor: '#d9d9d9',
      // },
      plotOptions: {
        series: {
          boostThreshold: 1,
          events: {
            legendItemClick: function (event: SeriesLegendItemClickEventObject): void {
              // eslint-disable-next-line @typescript-eslint/no-this-alias
              const that: Series = this;
              const visibleLegends = that.chart.legend.allItems.filter(legend => legend.visible);
              if (visibleLegends.length === 1 && visibleLegends[0].name === event.target.name) {
                event.preventDefault();
              }
            },
          },
          lineWidth: 2,
          marker: {
            radius: 1,
          },
          point: {
            events: {
              click: function (): void {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const point: Point = this;
                if (point.series.options.className?.indexOf('popup-on-click') !== -1) {
                  const chart = point.series.chart;
                  const date = displayDateTime(point.x, 'llll');
                  const text = `<b>${date}</b><br/>${point.y.toFixed(2)} ${unit} | ${point.series.name}`;

                  const anchorX = this.plotX + point.series.xAxis['pos'];
                  const anchorY = this.plotY + point.series.yAxis['pos'];
                  const align = anchorX < chart.chartWidth - 200 ? 'left' : 'right';
                  const x = align === 'left' ? anchorX + 10 : anchorX - 10;
                  const y = anchorY - 30;
                  if (!chart['sticky']) {
                    chart['sticky'] = chart.renderer
                      .label(text, x, y, 'callout', anchorX, anchorY)
                      .attr({
                        align,
                        fill: 'rgba(0, 0, 0, 0.75)',
                        padding: 10,
                        // Above series, below tooltip
                        style: { fontSize: '14px' },
                        zIndex: 7,
                      })
                      .css({
                        borderRadius: 10,
                        color: 'white',
                      })
                      .on('click', function () {
                        chart['sticky'] = chart['sticky'].destroy();
                      })
                      .add();
                  } else {
                    chart['sticky'].attr({ align, text }).animate({ anchorX, anchorY, x, y }, { duration: 250 });
                  }
                }
              },
              mouseOver: function () {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const that = this;
                // @ts-expect-error halo is expected to exist
                if (that.series.halo) {
                  // @ts-expect-error halo is expected to exist
                  that.series.halo
                    .attr({
                      class: 'highcharts-tracker',
                    })
                    .toFront();
                }
              },
            },
          },
          pointInterval: 90000,
          showInLegend: true,
          states: {
            hover: {
              enabled: true,
            },
          },
          turboThreshold: 0,
        },
      },
      // rangeSelector: {
      //   allButtonsEnabled: true,
      //   buttons: [
      //     {
      //       count: 1,
      //       text: this.translateService.instant('chart.range_selector.buttons.1m'),
      //       type: 'month',
      //     },
      //     {
      //       count: 3,
      //       text: this.translateService.instant('chart.range_selector.buttons.3m'),
      //       type: 'month',
      //     },
      //     {
      //       count: 6,
      //       text: this.translateService.instant('chart.range_selector.buttons.6m'),
      //       type: 'month',
      //     },
      //     {
      //       count: 1,
      //       text: this.translateService.instant('chart.range_selector.buttons.1y'),
      //       type: 'year',
      //     },
      //   ],
      //   buttonSpacing: 30,
      //   enabled: true,
      //   selected: 4,
      // },
      scrollbar: {
        barBackgroundColor: '#cacaca',
        barBorderColor: 'transparent',
        barBorderRadius: 6,
        barBorderWidth: 0,
        buttonArrowColor: 'transparent',
        buttonBackgroundColor: 'transparent',
        buttonBorderColor: 'transparent',
        height: 6,
        minWidth: 6,
        rifleColor: 'transparent',
        trackBackgroundColor: 'transparent',
        trackBorderColor: 'transparent',
      },
      series: this.series as SeriesOptionsType[],
      title: {
        align: 'left',
        margin: 25,
        style: {
          color: '#333333',
          fontSize: '14px',
        },
        text: '',
      },
      tooltip: {
        backgroundColor: '#fff',
        borderColor: '#979797',
        formatter: function (): string {
          return tooltipFormatter(this);
        },
        shadow: false,
        shared: true,
        useHTML: true,
      },
      xAxis: {
        angle: 0,
        endOnTick: false,
        labels: {
          allowOverlap: false,
          formatter: this.xAxisFormatter,
          rotation: -45,
          style: {
            color: '#999999',
            fontSize: '12px',
            paddingLeft: '5px',
            paddingRight: '5px',
            textAlign: 'center',
          },
          useHTML: true,
        },
        lineColor: '#ccd6eb',
        max: this.maxDate ? this.maxDate - new Date().getTimezoneOffset() * 60000 : undefined,
        maxPadding: 0,
        min: this.minDate ? this.minDate + new Date().getTimezoneOffset() * 60000 : undefined,
        minPadding: 0,
        plotBands: [
          {
            color: 'rgba(113, 113, 113, 0.3)',
            from: new Date().valueOf(),
            to: this.maxDate ? this.maxDate : undefined,
          },
        ],
        startOnTick: false,
        tickColor: '#ccd6eb',
        type: 'datetime',
      },
      yAxis: this.getYAxis(this.tabs ? this.tabs[this.activeTabIndex].units : this.unit),
    });
  }
}
